import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "./Reveal";

const SHOTS = [
  {
    id: "craving",
    src: "/screenshots/find-craving.png",
    alt: "Find Your Craving — the Foodie Crave Discover map with search, cuisine, hours, and price filters",
  },
  {
    id: "know",
    src: "/screenshots/know-before.png",
    alt: "Know Before You Go — a Foodie Crave restaurant page with hours, location, and contact info",
  },
  {
    id: "meal",
    src: "/screenshots/remember-meal.png",
    alt: "Remember That Meal — Foodie Crave My Places visits, notes, and reviews",
  },
  {
    id: "plan",
    src: "/screenshots/invite-plan.png",
    alt: "Invite & Plan Together — Foodie Crave dining invitations with friends, voting, and RSVPs",
  },
];

export const Screenshots = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % SHOTS.length), 4000);
    return () => clearInterval(id);
  }, []);

  const order = [
    (active + SHOTS.length - 1) % SHOTS.length,
    active,
    (active + 1) % SHOTS.length,
  ];

  return (
    <section
      id="screenshots"
      data-testid="screenshots-section"
      className="scroll-mt-24 overflow-hidden py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <Reveal className="text-center">
          <p className="text-xs font-bold uppercase tracking-[0.28em] text-crave-gold">Inside the App</p>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-4xl font-medium leading-[1.05] tracking-tight text-crave-cream sm:text-5xl">
            Designed to make mouths water
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-crave-muted">
            Real screens from the app — explore restaurants, save places, track visits, and invite
            company when you want it.
          </p>
        </Reveal>

        <div className="mt-16 flex items-center justify-center gap-4 md:gap-10" data-testid="screenshots-carousel">
          {order.map((idx, pos) => {
            const isActive = pos === 1;
            return (
              <motion.div
                key={SHOTS[idx].id}
                initial={false}
                animate={{
                  scale: isActive ? 1 : 0.82,
                  opacity: isActive ? 1 : 0.35,
                  y: isActive ? 0 : 24,
                }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className={`${isActive ? "" : "hidden md:block"}`}
              >
                <div className="overflow-hidden rounded-3xl border border-crave-border shadow-[0_30px_60px_-20px_rgba(0,0,0,0.8)]">
                  <img
                    src={SHOTS[idx].src}
                    alt={SHOTS[idx].alt}
                    loading="lazy"
                    className="h-auto w-[min(82vw,30rem)]"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-center gap-4">
          <button
            type="button"
            data-testid="carousel-prev-button"
            aria-label="Previous screenshot"
            onClick={() => setActive((a) => (a + SHOTS.length - 1) % SHOTS.length)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-crave-border text-crave-cream transition-colors duration-300 hover:border-crave-gold hover:text-crave-gold"
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="flex items-center gap-2">
            {SHOTS.map((s, i) => (
              <button
                key={s.id}
                type="button"
                data-testid={`carousel-dot-${i}`}
                aria-label={`Go to screenshot ${i + 1}`}
                onClick={() => setActive(i)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === active ? "w-8 bg-crave-gold" : "w-2 bg-crave-border hover:bg-crave-muted"
                }`}
              />
            ))}
          </div>
          <button
            type="button"
            data-testid="carousel-next-button"
            aria-label="Next screenshot"
            onClick={() => setActive((a) => (a + 1) % SHOTS.length)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-crave-border text-crave-cream transition-colors duration-300 hover:border-crave-gold hover:text-crave-gold"
          >
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
};
